/**
 * Minimal longitudinal model of an EV (Tesla-ish): instant torque, strong
 * regen/brakes, aero drag. Used by the mock provider to turn pedal/brake into
 * realistic speed and acceleration.
 */
export class SimulatedVehicle {
  /** m/s */
  speedMs = 0;
  /** m/s² (net, last tick) */
  accelMs2 = 0;

  constructor(
    private readonly maxAccel = 7.5,
    private readonly maxBrake = 9,
    private topSpeedMs = 72,
  ) {}

  /** the selected Tesla's (limited) top speed — full throttle reaches it asymptotically */
  setTopSpeedKmh(kmh: number): void {
    this.topSpeedMs = Math.max(20, kmh / 3.6);
  }

  tick(pedal: number, brake: number, dtS: number): void {
    const v = this.speedMs;
    const p = clamp01(pedal);
    const b = clamp01(brake);
    // tractive effort fades towards the car's top speed (reached at full pedal); drag only when lifting
    const tractive = p * this.maxAccel * Math.max(0, 1 - Math.pow(v / this.topSpeedMs, 2.2));
    const braking = b * this.maxBrake;
    const drag = v > 0 ? (1 - p) * (0.0012 * v * v + 0.12) : 0;
    let a = tractive - braking - drag;
    if (v <= 0 && a < 0) a = 0;
    const nv = Math.max(0, v + a * dtS);
    this.accelMs2 = (nv - v) / dtS;
    this.speedMs = nv;
  }

  get speedKmh(): number {
    return this.speedMs * 3.6;
  }
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
